import classNames from "classnames";
import uniqueId from "lodash/uniqueId";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import KuikaInput, { KuikaInputAutoSize } from "../kuika-input/kuika-input";
import KuikaOverflowText from "../kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../kuika-overflow-text/overflow-reveal-controller";
import {
  KuikaPopupContainerGetter,
  useKuikaPopupContainer
} from "../kuika-popup-container/kuika-popup-container-context";
import { defaultMentionFilter, findMention, IMentionInsertion, IMentionMatch, insertMention } from "./find-mention";
import { ICaretPopupPlacement, ICaretRect, measureCaret, placeCaretPopup } from "./measure-caret";
import "./kuika-mentions.scss";

export interface IKuikaMentionOption {
  /** What is inserted after the prefix, and what the default filter matches against. */
  value: string;
  /** What the row shows. */
  label: React.ReactNode;
  /** Matched instead of `value` by the default filter, when the two differ. */
  searchLabel?: string;
  disabled?: boolean;
  testId?: string;
}

export interface IKuikaMentionsProps {
  options: IKuikaMentionOption[];
  value?: string;
  defaultValue?: string;
  /** The full text after every edit, a pick included. */
  onChange?: (value: string) => void;
  /** The textarea's own blur event — `event.target.value` is the full text, a pick included. */
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  /** After a pick has been written into the text. */
  onSelect?: (option: IKuikaMentionOption, prefix: string) => void;
  /** What opens the list. Default "@". */
  prefix?: string;
  /** What a pick is followed by, and what ends a query. Default " ". */
  split?: string;
  placeholder?: string;
  disabled?: boolean;
  autoSize?: KuikaInputAutoSize;
  size?: "sm" | "md" | "lg";
  /** Default: case-insensitive "contains" on `searchLabel ?? value`. */
  filterOption?: (query: string, option: IKuikaMentionOption) => boolean;
  /** Shown when nothing matches. Without it an empty match shows no list at all. */
  notFoundContent?: React.ReactNode;
  className?: string;
  /** Lands on the textarea; the list gets `${dataTestId}List`. */
  dataTestId?: string;
}

/** Which input moved the highlight last — the pointer resting on a row must not light a second one. */
type MentionsNavigation = "pointer" | "keyboard";

interface IActiveOption {
  query: string;
  index: number;
}

type NavigationDirection = 1 | -1;

const DEFAULT_PREFIX: string = "@";
const DEFAULT_SPLIT: string = " ";
// The gap between the caret line and the list, defined on the popup in kuika-mentions.scss.
const OFFSET_PROPERTY: string = "--kuika-mentions-offset";
// Safari commits an IME composition with a keydown whose `isComposing` is already false; its
// keyCode is still 229, the "IME is processing this key" code.
const IME_PROCESS_KEY_CODE: number = 229;

// The same write KuikaInput's own `writeValue` does (kuika-input.tsx): a plain `node.value = …`
// leaves React's value tracker on the old string and the `input` event that follows is dismissed
// as "nothing changed". Through the prototype setter the event counts — `onChange` sees the pick
// in controlled and uncontrolled use alike, and a later blur reads the full text.
const writeTextareaValue = (textarea: HTMLTextAreaElement, next: string): void => {
  const setter: ((this: HTMLTextAreaElement, value: string) => void) | undefined = Object.getOwnPropertyDescriptor(
    HTMLTextAreaElement.prototype,
    "value"
  )?.set;
  if (setter) {
    setter.call(textarea, next);
  } else {
    textarea.value = next;
  }
  textarea.dispatchEvent(new Event("input", { bubbles: true }));
};

const firstEnabledIndex = (options: IKuikaMentionOption[]): number =>
  options.findIndex((option: IKuikaMentionOption): boolean => !option.disabled);

// The next enabled row in `direction`; the ends of the list are real edges, so it never wraps.
const stepEnabledIndex = (options: IKuikaMentionOption[], from: number, direction: NavigationDirection): number => {
  for (let index = from + direction; index >= 0 && index < options.length; index += direction) {
    if (!options[index].disabled) {
      return index;
    }
  }
  return from;
};

const hasRenderableContent = (node: React.ReactNode): boolean =>
  node !== undefined && node !== null && node !== false && node !== "";

const isComposing = (event: React.KeyboardEvent<HTMLTextAreaElement>): boolean =>
  event.nativeEvent.isComposing || event.keyCode === IME_PROCESS_KEY_CODE;

const toPixels = (value: string): number => {
  const parsed: number = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

// The anchor is a zero-size fixed box at the caret line; the list hangs from it (or stands on it).
// A transformed ancestor of the portal container — a drawer mid-slide, a popover's scale-in — is
// the containing block of a `position: fixed` box instead of the viewport, so the anchor is
// placed, then moved by however far it actually landed from where it was asked to go. The anchor
// itself never animates, so that reading is not skewed by the list's own entrance.
const positionAnchor = (anchor: HTMLDivElement, placement: ICaretPopupPlacement): void => {
  anchor.dataset.side = placement.side;
  anchor.style.left = `${placement.left}px`;
  anchor.style.top = `${placement.top}px`;
  const landed: DOMRect = anchor.getBoundingClientRect();
  const driftX: number = landed.left - placement.left;
  const driftY: number = landed.top - placement.top;
  if (driftX !== 0 || driftY !== 0) {
    anchor.style.left = `${placement.left - driftX}px`;
    anchor.style.top = `${placement.top - driftY}px`;
  }
};

// Scrolls the list, and only the list, to show `row`. `scrollIntoView` would also scroll whatever
// holds the field — and an ancestor scroll is what closes the list.
const scrollRowIntoView = (list: HTMLElement, row: HTMLElement): void => {
  if (row.offsetTop < list.scrollTop) {
    list.scrollTop = row.offsetTop;
  } else if (row.offsetTop + row.offsetHeight > list.scrollTop + list.clientHeight) {
    list.scrollTop = row.offsetTop + row.offsetHeight - list.clientHeight;
  }
};

/**
 * A textarea that offers a list of options when the reader types `prefix` (default "@"), and
 * writes `prefix + value + split` in place of what was typed when one is picked — the kit's
 * replacement for antd's `Mentions`, built on `KuikaInput type="textarea"`.
 *
 * The text stays plain: nothing marks a mention once it is written. The list follows the
 * keyboard (↑/↓ without wrapping, Enter/Tab to pick, Escape to dismiss without letting the
 * Escape reach a surrounding drawer or modal) and never takes focus — a pick by pointer keeps the
 * caret in the field, so a consumer that saves on blur never saves a half-typed query.
 */
const KuikaMentions: React.FC<IKuikaMentionsProps> = ({
  options,
  value,
  defaultValue,
  onChange,
  onBlur,
  onSelect,
  prefix = DEFAULT_PREFIX,
  split = DEFAULT_SPLIT,
  placeholder,
  disabled = false,
  autoSize,
  size = "md",
  filterOption = defaultMentionFilter,
  notFoundContent,
  className,
  dataTestId
}: IKuikaMentionsProps): JSX.Element => {
  const fieldRef: React.MutableRefObject<HTMLInputElement | HTMLTextAreaElement | null> = useRef<
    HTMLInputElement | HTMLTextAreaElement | null
  >(null);
  const anchorRef: React.MutableRefObject<HTMLDivElement | null> = useRef<HTMLDivElement | null>(null);
  const listRef: React.MutableRefObject<HTMLDivElement | null> = useRef<HTMLDivElement | null>(null);
  // Where the caret belongs after a pick, re-applied after the render the pick causes: a
  // controlled parent writing its `value` back must not leave the caret at the end of the text.
  const pendingCaretRef: React.MutableRefObject<number | null> = useRef<number | null>(null);
  const [listId] = useState<string>((): string => uniqueId("kuika-mentions-list-"));
  const [match, setMatch] = useState<IMentionMatch | null>(null);
  // Escape dismisses the list for the mention it was open on, until the caret leaves that mention.
  const [dismissedStart, setDismissedStart] = useState<number | null>(null);
  const [active, setActive] = useState<IActiveOption | null>(null);
  const [navigation, setNavigation] = useState<MentionsNavigation>("pointer");
  // Bumped when the field scrolls under an open list, so the list follows the caret line.
  const [layoutPass, setLayoutPass] = useState<number>(0);
  // Where the list mounts: the nearest KuikaPopupContainerContext surface (a drawer's body lock
  // would leave a list on <body> painted but unclickable), else the document's body. Resolved
  // from the field when a mention is found, since the context getter wants the trigger node.
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const getPopupContainer: KuikaPopupContainerGetter | undefined = useKuikaPopupContainer();

  const getTextarea = useCallback((): HTMLTextAreaElement | null => {
    const node: HTMLInputElement | HTMLTextAreaElement | null = fieldRef.current;
    return node instanceof HTMLTextAreaElement ? node : null;
  }, []);

  const filteredOptions: IKuikaMentionOption[] = useMemo(
    (): IKuikaMentionOption[] =>
      match === null ? [] : options.filter((option: IKuikaMentionOption): boolean => filterOption(match.query, option)),
    [options, match, filterOption]
  );

  const showsNotFound: boolean = hasRenderableContent(notFoundContent);
  const isOpen: boolean =
    !disabled && match !== null && match.start !== dismissedStart && (filteredOptions.length > 0 || showsNotFound);
  const query: string = match?.query ?? "";
  // A new query starts the highlight over on its first enabled row.
  const activeIndex: number =
    active !== null && active.query === query && active.index < filteredOptions.length
      ? active.index
      : firstEnabledIndex(filteredOptions);
  const optionId = (index: number): string => `${listId}-option-${index}`;

  const closeList = useCallback((): void => {
    setMatch(null);
    setDismissedStart(null);
    setActive(null);
  }, []);

  const detectMention = (): void => {
    const textarea: HTMLTextAreaElement | null = getTextarea();
    if (textarea === null || disabled || textarea.selectionStart !== textarea.selectionEnd) {
      closeList();
      return;
    }
    const next: IMentionMatch | null = findMention(textarea.value, textarea.selectionStart, prefix, split);
    setMatch(next);
    if (next === null || next.start !== dismissedStart) {
      setDismissedStart(null);
    }
    if (next !== null) {
      setContainer(getPopupContainer?.(textarea) ?? textarea.ownerDocument.body);
    }
  };

  const insertOption = (option: IKuikaMentionOption): void => {
    const textarea: HTMLTextAreaElement | null = getTextarea();
    if (option.disabled || textarea === null) {
      return;
    }
    // Read afresh: the caret is where the pick goes, whatever was last detected.
    const current: IMentionMatch | null = findMention(textarea.value, textarea.selectionStart, prefix, split);
    if (current === null) {
      closeList();
      return;
    }
    const insertion: IMentionInsertion = insertMention(textarea.value, current, option.value, prefix, split);
    pendingCaretRef.current = insertion.caret;
    writeTextareaValue(textarea, insertion.text);
    textarea.setSelectionRange(insertion.caret, insertion.caret);
    closeList();
    onSelect?.(option, prefix);
  };

  const handleChange = (_event: React.ChangeEvent<HTMLTextAreaElement>, next: string | number): void => {
    onChange?.(String(next));
    detectMention();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (isComposing(event) || !isOpen) {
      return;
    }
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp": {
        event.preventDefault();
        const direction: NavigationDirection = event.key === "ArrowDown" ? 1 : -1;
        setActive({ query, index: stepEnabledIndex(filteredOptions, activeIndex, direction) });
        setNavigation("keyboard");
        return;
      }
      case "Enter":
      case "Tab": {
        const option: IKuikaMentionOption | undefined = filteredOptions[activeIndex];
        // Nothing to pick (only the not-found line): Enter breaks the line, Tab leaves the field.
        if (option === undefined || option.disabled || (event.key === "Tab" && event.shiftKey)) {
          return;
        }
        event.preventDefault();
        insertOption(option);
        return;
      }
      case "Escape": {
        // The list's Escape, not the surrounding drawer's or modal's.
        event.preventDefault();
        event.stopPropagation();
        setDismissedStart(match === null ? null : match.start);
        setActive(null);
        return;
      }
      default:
        return;
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLTextAreaElement>): void => {
    closeList();
    onBlur?.(event);
  };

  const handleOptionPointer = (index: number): void => {
    if (navigation !== "pointer") {
      setNavigation("pointer");
    }
    if (index !== activeIndex) {
      setActive({ query, index });
    }
  };

  // A press anywhere on the list must not take focus from the field: blur is when a consumer
  // saves, and a pick must not save the half-typed query first.
  const keepFieldFocused = (event: React.MouseEvent<HTMLDivElement>): void => {
    event.preventDefault();
  };

  useLayoutEffect((): void => {
    const caret: number | null = pendingCaretRef.current;
    const textarea: HTMLTextAreaElement | null = getTextarea();
    if (caret === null || textarea === null) {
      return;
    }
    pendingCaretRef.current = null;
    if (caret <= textarea.value.length && (textarea.selectionStart !== caret || textarea.selectionEnd !== caret)) {
      textarea.setSelectionRange(caret, caret);
    }
  });

  useLayoutEffect((): void => {
    const textarea: HTMLTextAreaElement | null = getTextarea();
    const anchor: HTMLDivElement | null = anchorRef.current;
    const list: HTMLDivElement | null = listRef.current;
    if (!isOpen || match === null || textarea === null || anchor === null || list === null) {
      return;
    }
    const view: Window = textarea.ownerDocument.defaultView ?? window;
    // Anchored where the mention starts, not at the moving caret: the list stays put while the
    // query is typed, the way antd's did.
    const caret: ICaretRect = measureCaret(textarea, match.start);
    const offset: number = toPixels(view.getComputedStyle(list).getPropertyValue(OFFSET_PROPERTY));
    const placement: ICaretPopupPlacement = placeCaretPopup(
      caret,
      { width: list.offsetWidth, height: list.offsetHeight },
      { width: view.innerWidth, height: view.innerHeight },
      offset
    );
    positionAnchor(anchor, placement);
  }, [isOpen, match, filteredOptions.length, layoutPass, getTextarea]);

  useLayoutEffect((): void => {
    const list: HTMLDivElement | null = listRef.current;
    if (!isOpen || navigation !== "keyboard" || list === null || activeIndex < 0) {
      return;
    }
    const row: HTMLElement | null = list.querySelector<HTMLElement>(`[id="${listId}-option-${activeIndex}"]`);
    if (row !== null) {
      scrollRowIntoView(list, row);
    }
  }, [isOpen, navigation, activeIndex, listId]);

  useEffect((): (() => void) | undefined => {
    const textarea: HTMLTextAreaElement | null = getTextarea();
    if (!isOpen || textarea === null) {
      return undefined;
    }
    const ownerDocument: Document = textarea.ownerDocument;
    const view: Window = ownerDocument.defaultView ?? window;
    // The field's own scroll moves the caret line under the list: follow it. Anything that holds
    // the field scrolling away leaves the list pointing at nothing: close it. The list's own
    // scroll holds neither and is left alone.
    const handleScroll = (event: Event): void => {
      const target: EventTarget | null = event.target;
      if (target === textarea) {
        setLayoutPass((pass: number): number => pass + 1);
      } else if (target instanceof Node && target.contains(textarea)) {
        closeList();
      }
    };
    ownerDocument.addEventListener("scroll", handleScroll, true);
    view.addEventListener("resize", closeList);
    return (): void => {
      ownerDocument.removeEventListener("scroll", handleScroll, true);
      view.removeEventListener("resize", closeList);
    };
  }, [isOpen, getTextarea, closeList]);

  const popup: React.ReactPortal | null =
    !isOpen || container === null
      ? null
      : createPortal(
          <div ref={anchorRef} className="kuika-mentions__anchor">
            <div
              ref={listRef}
              id={listId}
              role="listbox"
              className={classNames("kuika-mentions__popup", {
                "kuika-mentions__popup--keyboard": navigation === "keyboard"
              })}
              data-testid={dataTestId ? `${dataTestId}List` : undefined}
              onMouseDown={keepFieldFocused}
            >
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option: IKuikaMentionOption, index: number): JSX.Element => (
                  <div
                    key={option.value}
                    id={optionId(index)}
                    role="option"
                    aria-selected={index === activeIndex}
                    aria-disabled={option.disabled ? true : undefined}
                    className={classNames("kuika-mentions__option", OVERFLOW_REVEAL_HOST_CLASS, {
                      "kuika-mentions__option--active": index === activeIndex
                    })}
                    data-testid={option.testId}
                    onMouseMove={(): void => handleOptionPointer(index)}
                    onClick={(): void => insertOption(option)}
                  >
                    <KuikaOverflowText>{option.label}</KuikaOverflowText>
                  </div>
                ))
              ) : (
                <div role="option" aria-selected={false} aria-disabled className="kuika-mentions__empty">
                  {notFoundContent}
                </div>
              )}
            </div>
          </div>,
          container
        );

  return (
    <>
      <KuikaInput
        ref={fieldRef}
        type="textarea"
        className={classNames("kuika-mentions", className)}
        size={size}
        value={value}
        defaultValue={defaultValue}
        placeholder={placeholder}
        disabled={disabled}
        autoSize={autoSize}
        // A field that sizes itself to its text has no use for a drag handle that fights it.
        resizable={autoSize === undefined}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onSelect={detectMention}
        role="combobox"
        aria-autocomplete="list"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listId : undefined}
        aria-activedescendant={isOpen && activeIndex >= 0 ? optionId(activeIndex) : undefined}
        data-testid={dataTestId}
      />
      {popup}
    </>
  );
};

export default KuikaMentions;
